import type { CSSProperties } from 'react';
import styles from './BrandOwl.module.css';

/** Natural aspect ratio of /logo-owl.png (660 × 949, the owl perched on a book). */
export const OWL_ASPECT = 660 / 949;

export interface BrandOwlProps {
  /** Rendered height in px (width follows the owl's 660:949 ratio unless `width` is set). */
  height: number;
  /** Explicit width; overrides the natural ratio. */
  width?: number;
  /**
   * `color`: the red owl on its book, the white eye mask, beak and eye highlights cut out (/logo-owl.png);
   * `mono`: tintable silhouette, eye mask cut out and a gap around the pages (mask of /icons/badge-96.png filled with currentColor);
   * `tile`: white app-icon tile (/icons/icon-512.png).
   */
  variant?: 'color' | 'mono' | 'tile';
  /** Accessible label; decorative when omitted. */
  alt?: string;
  className?: string;
  style?: CSSProperties;
}

/** The Plume owl in its shipped renditions (spec 12 §1, §4.4; an owl perched on a book replaces Flutter's owl). */
export function BrandOwl({ height, width, variant = 'color', alt, className, style }: BrandOwlProps) {
  const classes = [styles.owl, className ?? ''].filter(Boolean).join(' ');
  if (variant === 'mono') {
    const w = width ?? height;
    return (
      <span
        className={[classes, styles.mono].join(' ')}
        style={{ width: w, height, ...style }}
        role={alt ? 'img' : undefined}
        aria-label={alt}
        aria-hidden={alt ? undefined : true}
      />
    );
  }
  const src = variant === 'tile' ? '/icons/icon-512.png' : '/logo-owl.png';
  const w = width ?? (variant === 'tile' ? height : Math.round(height * OWL_ASPECT));
  return <img className={classes} src={src} width={w} height={height} alt={alt ?? ''} draggable={false} style={style} decoding="async" />;
}
